import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate, timeAgo } from "@/lib/format";
import { ArrowLeft, DownloadSimple, MagnifyingGlass, Eye, ChatCircleText, Handshake, Bookmark, SignIn, ShieldCheck } from "@phosphor-icons/react";

const ACTION_ICONS = {
  login: SignIn,
  company_view: Eye,
  document_view: Eye,
  enquiry_submit: ChatCircleText,
  meeting_request: Handshake,
  bookmark_add: Bookmark,
  bookmark_remove: Bookmark,
  declaration_accept: ShieldCheck,
  nda_accept: ShieldCheck,
};

const ACTION_LABELS = {
  login: "Signed in",
  company_view: "Viewed company",
  document_view: "Viewed document",
  enquiry_submit: "Submitted enquiry",
  meeting_request: "Requested meeting",
  bookmark_add: "Added to watchlist",
  bookmark_remove: "Removed from watchlist",
  declaration_accept: "Accepted FCA declaration",
  nda_accept: "Accepted NDA",
};

const KpiCard = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">{label}</div>
    <div className="font-display text-3xl font-bold num-display">{value ?? "—"}</div>
  </div>
);

export default function AdminUserAudit() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/admin/users/${id}/audit`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [id]);

  const filteredActivity = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.activity;
    return data.activity.filter((a) => {
      const s = `${a.action || ""} ${a.company_name || ""} ${a.document_name || ""} ${a.ip || ""}`.toLowerCase();
      return s.includes(term);
    });
  }, [data, q]);

  const downloadCsv = () => {
    const token = localStorage.getItem("ip_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${id}/audit.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_${data?.user?.email || id}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      });
  };

  if (loading) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  if (!data) return <div className="p-8 text-muted-foreground text-sm">Not found.</div>;
  const u = data.user;
  const s = data.stats;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={13} /> Back to Users
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Compliance · Investor Audit</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{u.name}</h1>
          <div className="mt-1 text-muted-foreground text-sm">{u.email}{u.company ? <> · {u.company}</> : null}</div>
        </div>
        <Button onClick={downloadCsv} data-testid="download-audit-csv" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
          <DownloadSimple size={15} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Profile grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Classification" value={u.classification || "—"} />
        <KpiCard label="Approval status" value={u.approval_status || "—"} />
        <KpiCard label="Date registered" value={u.created_at ? formatDate(u.created_at) : "—"} />
        <KpiCard label="Date approved" value={u.approved_at ? formatDate(u.approved_at) : "—"} />
        <KpiCard label="Approved by" value={u.approved_by_name || "—"} />
        <KpiCard label="Last login" value={u.last_login ? timeAgo(u.last_login) : "—"} />
        <KpiCard label="Companies viewed" value={s.companies_viewed} />
        <KpiCard label="Documents viewed" value={s.documents_viewed} />
        <KpiCard label="Meeting requests" value={s.meeting_requests} />
        <KpiCard label="Enquiries" value={s.enquiries} />
        <KpiCard label="Bookmarks" value={s.bookmarks} />
        <KpiCard label="Compliance docs" value={s.compliance_docs} />
      </div>

      {/* Declaration audit strip */}
      {u.declaration_accepted_at && (
        <div className="mb-6 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/30 p-4 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} weight="fill" className="text-emerald-700 dark:text-emerald-400" />
            <span className="font-semibold text-emerald-900 dark:text-emerald-200">Declaration accepted</span>
          </div>
          <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80">
            <span className="num-display">{formatDate(u.declaration_accepted_at)}</span>
            {u.declaration_ip && <> · IP <span className="font-mono">{u.declaration_ip}</span></>}
            {u.declaration_versions && Object.keys(u.declaration_versions).length > 0 && (
              <> · Versions accepted: {Object.keys(u.declaration_versions).join(", ")}</>
            )}
          </div>
        </div>
      )}

      {/* Activity */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border bg-secondary/40">
          <div className="font-display font-bold text-lg">Activity Log</div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 h-9 w-64" data-testid="audit-search" />
          </div>
        </div>
        {filteredActivity.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm italic">No activity to show.</div>
        ) : (
          <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
            {filteredActivity.map((row) => {
              const Icon = ACTION_ICONS[row.action] || Eye;
              return (
                <div key={row.id} className="grid grid-cols-[24px_1fr_auto] items-center gap-3 px-5 py-2.5 hover:bg-secondary/30">
                  <Icon size={14} className="text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {ACTION_LABELS[row.action] || row.action}
                      {row.company_name && <> — <span className="text-emerald-700 dark:text-emerald-400">{row.company_name}</span></>}
                      {row.document_name && <> — <span className="font-mono text-xs">{row.document_name}</span></>}
                    </div>
                    <div className="text-[11px] text-muted-foreground num-display">
                      {formatDate(row.created_at)}{row.ip ? ` · ${row.ip}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
